import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  MultiSelect,
  Select,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useSourceSubsites,
  useCreateSourceSubsite,
  useUpdateSourceSubsite,
  useToggleSourceSubsiteActive,
} from "../../../hooks/admin/useSourceSubsites";
import { useSourcesAdmin } from "../../../hooks/admin/useSources";
import SourceSubsiteForm, {
  SourceSubsiteFormValues,
} from "../../../components/admin/sourcesubsites/SourceSubsiteForm";
import { SourceSubsite } from "../../../services/admin/sourcesubsites.service";

interface SubsiteFilters {
  sourceIds?: string[];
  status?: "all" | "active" | "inactive";
}

const ITEMS_PER_PAGE = 20;

export default function SourceSubsitesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editingSubsite, setEditingSubsite] = useState<SourceSubsite | null>(
    null
  );
  const [subsiteToToggle, setSubsiteToToggle] = useState<SourceSubsite | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<SubsiteFilters>({});
  const [pendingFilters, setPendingFilters] = useState<SubsiteFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: subsites = [], isLoading, refetch } = useSourceSubsites();
  const { data: sources = [] } = useSourcesAdmin();
  const createSubsite = useCreateSourceSubsite();
  const updateSubsite = useUpdateSourceSubsite();
  const toggleSubsiteActive = useToggleSourceSubsiteActive();

  // Filter data based on search and filters
  const filteredSubsites = useMemo(() => {
    let result = subsites;

    // Apply search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (subsite) =>
          subsite.name.toLowerCase().includes(searchLower) ||
          subsite.description?.toLowerCase().includes(searchLower) ||
          subsite.source?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply source filter
    if (filters.sourceIds && filters.sourceIds.length > 0) {
      result = result.filter(
        (subsite) =>
          subsite.sourceId && filters.sourceIds!.includes(subsite.sourceId)
      );
    }

    // Apply status filter
    if (filters.status === "active") {
      result = result.filter((subsite) => subsite.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((subsite) => !subsite.isActive);
    }

    // Sort by subsite name
    result = [...result].sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [subsites, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredSubsites.length / ITEMS_PER_PAGE);
  const paginatedSubsites = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredSubsites.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSubsites, page]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.sourceIds && filters.sourceIds.length > 0) ||
    (filters.status && filters.status !== "all");

  // Handlers
  const handleAdd = () => {
    setEditingSubsite(null);
    openFormModal();
  };

  const handleEdit = (subsite: SourceSubsite) => {
    setEditingSubsite(subsite);
    openFormModal();
  };

  const handleSubmit = async (values: SourceSubsiteFormValues) => {
    if (editingSubsite) {
      await updateSubsite.mutateAsync({
        id: editingSubsite.id,
        ...values,
      });
    } else {
      await createSubsite.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (subsite: SourceSubsite) => {
    setSubsiteToToggle(subsite);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (subsiteToToggle) {
      await toggleSubsiteActive.mutateAsync({
        id: subsiteToToggle.id,
        isActive: !subsiteToToggle.isActive,
      });
      closeConfirmModal();
      setSubsiteToToggle(null);
    }
  };

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  // Dropdown options
  const sourceOptions = sources
    .filter((s) => s.isActive !== false)
    .map((s) => ({ value: s.id, label: s.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Source Subsites</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage anatomical subsites for specimen sources
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name, description, or source..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.sourceIds && filters.sourceIds.length > 0 && (
                      <Badge variant="light" color="blue">
                        {filters.sourceIds.length} Source
                        {filters.sourceIds.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {filters.status && filters.status !== "all" && (
                      <Badge variant="light" color="green">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredSubsites.length} subsite
                  {filteredSubsites.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Source Subsites ({filteredSubsites.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredSubsites.length)}{" "}
                      of {filteredSubsites.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredSubsites.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No subsites match the current filters"
                    : "No source subsites found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Subsite Name</Table.Th>
                      <Table.Th>Source</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedSubsites.map((subsite) => (
                      <Table.Tr
                        key={subsite.id}
                        style={{
                          opacity: subsite.isActive ? 1 : 0.5,
                        }}
                      >
                        {/* Subsite Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {subsite.name}
                          </Text>
                        </Table.Td>

                        {/* Source */}
                        <Table.Td>
                          <Badge color="blue" variant="light">
                            {subsite.source?.name || "—"}
                          </Badge>
                        </Table.Td>

                        {/* Description */}
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {subsite.description || "—"}
                          </Text>
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={subsite.isActive ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {subsite.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(subsite)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={
                                subsite.isActive ? "Deactivate" : "Activate"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                color={subsite.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(subsite)}
                              >
                                {subsite.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Subsite Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editingSubsite ? "Edit Source Subsite" : "Add New Source Subsite"
          }
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <SourceSubsiteForm
              initialValues={editingSubsite || undefined}
              onSubmit={handleSubmit}
              sources={sources}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createSubsite.isPending || updateSubsite.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingSubsite ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Source Subsites"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Sources"
              placeholder="Select sources..."
              data={sourceOptions}
              value={pendingFilters.sourceIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  sourceIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: (value as SubsiteFilters["status"]) || undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            subsiteToToggle?.isActive
              ? "Deactivate Subsite"
              : "Activate Subsite"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {subsiteToToggle?.isActive ? "deactivate" : "activate"} the
              subsite <strong>{subsiteToToggle?.name}</strong>?
            </Text>
            {subsiteToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated subsites will no longer be available for selection.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={subsiteToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleSubsiteActive.isPending}
              >
                {subsiteToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
